import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import AdminLayout from "@/components/admin/AdminLayout"
import { createAdminOrder } from "@/api/orderApi"
import { axiosInstance } from "@/api/axiosInstance"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Trash2, Search, X } from "lucide-react"

const QUICK_PHRASES = [
  "祝發展(축발전)", "祝當選(축당선)", "祝展示會(축전시회)", "삼가 故人의 冥福을 빕니다",
  "謹弔(근조)", "祝結婚", "祝華婚", "祝昇進(축승진)", "祝榮轉(축영전)", "대박나세요",
  "無窮한 發展을 祈願합니다", "祝就任", "就任을 祝賀드립니다", "당선을 축하드립니다",
  "極樂往生發願 극락왕생발원", "召天(소천)"
]

export default function AdminOrderCreatePage() {
  const navigate = useNavigate()

  // 1. 주문 마스터 폼 데이터 (이메일 및 결제수단 추가)
  const [formData, setFormData] = useState({
    ordererName: "", ordererPhone: "", ordererEmail: "",
    receiverName: "", receiverPhone: "",
    zipcode: "", address1: "", address2: "", deliveryMemo: "",
    shippingAmount: 0, discountAmount: 0,
    paymentMethod: "card" // 기본값: 신용카드(PG결제)
  })

  // 2. 주문 상품 배열 상태 관리
  const [orderItems, setOrderItems] = useState<any[]>([])

  // 3. 상품 검색 모달 관련 상태
  const [isProductModalOpen, setIsProductModalOpen] = useState(false)
  const [searchKeyword, setSearchKeyword] = useState("")
  const [searchResults, setSearchResults] = useState<any[]>([])
  const [isSearching, setIsSearching] = useState(false)

  // 💡 결제 진행 상태
  const [submitting, setSubmitting] = useState(false)

  // 4. 메시지 관리(리본/카드) 관련 상태
  const [messageType, setMessageType] = useState<"none" | "ribbon" | "card" | "ribbon_card">("none")
  const [ribbonPhrase, setRibbonPhrase] = useState("")
  const [ribbonSender, setRibbonSender] = useState("")
  const [cardContent, setCardContent] = useState("")
  const [isPhraseModalOpen, setIsPhraseModalOpen] = useState(false)
  const [isSenderModalOpen, setIsSenderModalOpen] = useState(false)

  // 5. 배송날짜 및 행사일정 상태
  const [deliveryDate, setDeliveryDate] = useState(() => {
    const today = new Date();
    const kstOffset = 9 * 60 * 60 * 1000;
    const kstDate = new Date(today.getTime() + kstOffset);
    return kstDate.toISOString().split('T')[0];
  })
  const [deliveryDay, setDeliveryDay] = useState("")
  const [deliveryType, setDeliveryType] = useState<"immediate" | "specified">("immediate")
  const [deliveryHour, setDeliveryHour] = useState("09")
  const [deliveryMinute, setDeliveryMinute] = useState("00")
  const [eventTime, setEventTime] = useState("")

  useEffect(() => {
    if (!deliveryDate) {
      setDeliveryDay("")
      return
    }
    const days = ['일요일', '월요일', '화요일', '수요일', '목요일', '금요일', '토요일']
    const dateObj = new Date(deliveryDate)
    if (!isNaN(dateObj.getTime())) {
      setDeliveryDay(days[dateObj.getDay()])
    }
  }, [deliveryDate])

  const openPostcode = () => {
    const daum = (window as any).daum
    if (!daum) return alert("주소 검색 스크립트가 로드되지 않았습니다.")
    new daum.Postcode({
      oncomplete: function(data: any) {
        setFormData(prev => ({ ...prev, zipcode: data.zonecode, address1: data.address, address2: "" }))
      }
    }).open()
  }

  const handleSearchProducts = async () => {
    if (!searchKeyword.trim()) return alert("검색어를 입력해주세요.")
    try {
      setIsSearching(true)
      const response = await axiosInstance.get("/api/admin/products", {
        params: { productName: searchKeyword, size: 50 }
      })
      const items = response.data?.data?.items || [];
      if (Array.isArray(items)) {
        setSearchResults(items)
      } else {
        setSearchResults([])
      }
    } catch (error) {
      console.error("상품 검색 실패:", error)
      setSearchResults([])
      alert("상품을 검색하는 중 오류가 발생했습니다.")
    } finally {
      setIsSearching(false)
    }
  }

  const handleSelectProduct = (product: any) => {
    if (orderItems.some(item => item.productId === product.productId)) {
      return alert("이미 추가된 상품입니다.")
    }
    setOrderItems(prev => [
      ...prev,
      {
        productId: product.productId,
        productNameSnapshot: product.productName,
        productPriceSnapshot: product.price,
        quantity: 1
      }
    ])
    setIsProductModalOpen(false)
  }

  const removeItem = (index: number) => {
    setOrderItems(orderItems.filter((_, i) => i !== index))
  }

  const handleItemChange = (index: number, field: string, value: any) => {
    const newItems = [...orderItems]
    newItems[index] = { ...newItems[index], [field]: value }
    setOrderItems(newItems)
  }

  const productAmount = orderItems.reduce((acc, item) => acc + (item.productPriceSnapshot * item.quantity), 0)
  const totalAmount = productAmount + formData.shippingAmount - formData.discountAmount

  // ==========================================
  // 💡 결제 연동 및 저장 로직 (완전판)
  // ==========================================
  const handlePayment = async () => {
    if (!formData.ordererName || !formData.receiverName) return alert("주문자 및 수령인명은 필수입니다.")
    if (formData.paymentMethod === 'card' && !formData.ordererEmail) return alert("PG결제를 위해 주문자 이메일을 입력해주세요.")
    if (orderItems.length === 0) return alert("주문할 상품을 추가해주세요.")

    // 1. 공통 백엔드 페이로드 구성
    const payload: any = {
      ...formData,
      productAmount, totalAmount,
      orderStatus: "PAID",
      paymentMethod: formData.paymentMethod.toUpperCase(),
      orderItems,
      messageType,
      ribbonPhrase: ["ribbon", "ribbon_card"].includes(messageType) ? ribbonPhrase : "",
      ribbonSender: ["ribbon", "ribbon_card"].includes(messageType) ? ribbonSender : "",
      cardContent: ["card", "ribbon_card"].includes(messageType) ? cardContent : "",
      deliveryDate, deliveryType,
      deliveryTime: deliveryType === "specified" ? `${deliveryHour}:${deliveryMinute}` : null,
      eventTime
    }

    // 2. 무통장/수기결제 선택 시 (PG창 띄우지 않고 바로 백엔드 저장)
    if (formData.paymentMethod === 'offline') {
      try {
        setSubmitting(true)
        await createAdminOrder(payload)
        alert("수기 주문서가 성공적으로 등록되었습니다.")
        navigate("/admin/orders")
      } catch (error) {
        alert("주문 등록에 실패했습니다.")
      } finally {
        setSubmitting(false)
      }
      return;
    }

    // 3. 신용카드(PG) 결제 선택 시
    const PortOne = (window as any).PortOne
    if (!PortOne) return alert("결제 모듈을 불러오지 못했습니다.")

    setSubmitting(true)
    const INICIS_CHANNEL_KEY = "channel-key-33f17fde-5956-482b-a4c0-0c9eb7455398"

    try {
      // 모바일 리다이렉트를 대비하여 어드민 결제 데이터 임시 저장
      sessionStorage.setItem("ADMIN_ORDER_TEMP_DATA", JSON.stringify(payload));

      const response = await PortOne.requestPayment({
        storeId: "store-a5787d08-083d-4f6d-97e3-52a9fd8d9ea4",
        channelKey: INICIS_CHANNEL_KEY,
        paymentId: `admin_mid_${new Date().getTime()}`,
        orderName: orderItems[0].productNameSnapshot + (orderItems.length > 1 ? ` 외 ${orderItems.length - 1}건` : ""),
        totalAmount: totalAmount,
        currency: "CURRENCY_KRW",
        payMethod: "CARD",
        customer: {
          fullName: formData.ordererName,
          phoneNumber: formData.ordererPhone,
          email: formData.ordererEmail,
          address: {
            addressLine1: formData.address1,
            addressLine2: formData.address2,
            zipcode: formData.zipcode,
          }
        },
        redirectUrl: `${window.location.origin}/admin/payment/callback`,
      })

      if (response.code !== undefined) {
        alert(`결제에 실패하였습니다. (${response.message})`)
        setSubmitting(false)
        return
      }

      // PC에서 결제가 성공적으로 끝났을 때
      payload.impUid = response.paymentId;
      await createAdminOrder(payload)
      sessionStorage.removeItem("ADMIN_ORDER_TEMP_DATA")
      alert("결제 및 주문서 등록이 완료되었습니다.")
      navigate("/admin/orders")

    } catch (error: any) {
      console.error(error)
      alert(`결제 처리 중 오류가 발생했습니다.`)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-5xl mx-auto pb-20 relative">
        <div className="flex items-end justify-between">
          <div>
            <h2 className="text-3xl font-bold">수기 주문서 작성</h2>
            <p className="mt-2 text-sm text-slate-500">고객의 전화/오프라인 주문을 등록하거나 직접 결제를 진행합니다.</p>
          </div>
        </div>

        <Card className="rounded-[28px] border border-slate-200 shadow-sm">
          <CardContent className="p-8 space-y-8">

            {/* 1. 주문자 & 수령인 영역 */}
            <section className="space-y-6 border-b border-slate-100 pb-8">
              <div className="bg-slate-50/50 border border-slate-100 p-6 rounded-2xl shadow-sm">
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <span className="w-1.5 h-4 bg-slate-900 rounded-full inline-block"></span>
                  주문자 정보
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label className="mb-2 block">주문자명 *</Label>
                    <Input value={formData.ordererName} onChange={e => setFormData({...formData, ordererName: e.target.value})} className="bg-white" />
                  </div>
                  <div>
                    <Label className="mb-2 block">연락처</Label>
                    <Input value={formData.ordererPhone} onChange={e => setFormData({...formData, ordererPhone: e.target.value})} className="bg-white" placeholder="010-0000-0000" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="mb-2 block">이메일 (PG 결제 시 필수) *</Label>
                    <Input type="email" value={formData.ordererEmail} onChange={e => setFormData({...formData, ordererEmail: e.target.value})} className="bg-white" placeholder="이메일을 입력해주세요." />
                  </div>
                </div>
              </div>

              <div className="bg-slate-50/50 border border-slate-100 p-6 rounded-2xl shadow-sm">
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <span className="w-1.5 h-4 bg-slate-900 rounded-full inline-block"></span>
                  수령인 배송지 정보
                </h3>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <Label className="mb-2 block">수령인명 *</Label>
                    <Input value={formData.receiverName} onChange={e => setFormData({...formData, receiverName: e.target.value})} className="bg-white" />
                  </div>
                  <div>
                    <Label className="mb-2 block">연락처</Label>
                    <Input value={formData.receiverPhone} onChange={e => setFormData({...formData, receiverPhone: e.target.value})} className="bg-white" placeholder="010-0000-0000" />
                  </div>
                </div>
                <div className="mb-6">
                  <Label className="block mb-2">배송지 주소 *</Label>
                  <div className="flex gap-2 mb-2">
                    <Input value={formData.zipcode} readOnly placeholder="우편번호" className="w-24 bg-white" />
                    <Button type="button" variant="outline" onClick={openPostcode} className="bg-white hover:bg-slate-100">우편번호 검색</Button>
                  </div>
                  <Input value={formData.address1} readOnly placeholder="기본 주소" className="bg-white mb-2" />
                  <Input value={formData.address2} onChange={e => setFormData({...formData, address2: e.target.value})} placeholder="상세 주소" className="bg-white" />
                </div>
                <div>
                  <Label className="mb-2 block">배송 메모 / 요청사항</Label>
                  <Input value={formData.deliveryMemo} onChange={e => setFormData({...formData, deliveryMemo: e.target.value})} className="bg-white" />
                </div>
              </div>
            </section>

            {/* 2. 배송날짜 및 행사 일정 영역 */}
            <section className="border-b border-slate-100 pb-8">
              <h3 className="text-lg font-bold mb-5 flex items-center gap-2 text-slate-900">
                <span className="w-1.5 h-4 bg-slate-900 rounded-full inline-block"></span>
                배송 및 행사 일정
              </h3>
              <div className="bg-slate-50 border border-slate-200 p-5 rounded-2xl shadow-sm flex flex-wrap items-center gap-y-4 gap-x-3 text-sm">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-slate-700 w-[60px]">배송날짜</span>
                  <Input type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} className="w-36 bg-white h-9" />
                  <Input readOnly value={deliveryDay} className="w-20 bg-slate-100 text-center text-slate-500 cursor-not-allowed h-9" />
                </div>

                <select
                  className="h-9 rounded-md border border-slate-200 bg-white px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  value={deliveryType}
                  onChange={(e) => setDeliveryType(e.target.value as "immediate" | "specified")}
                >
                  <option value="immediate">즉시배송</option>
                  <option value="specified">시간지정</option>
                </select>

                {deliveryType === "specified" && (
                  <div className="flex items-center gap-2 before:content-['/'] before:text-slate-300 before:mx-1 animate-in fade-in zoom-in-95 duration-200">
                    <span className="font-bold text-slate-700">배송시간</span>
                    <select className="h-9 rounded-md border border-slate-200 bg-white px-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" value={deliveryHour} onChange={(e) => setDeliveryHour(e.target.value)}>
                      {Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0')).map(h => (<option key={h} value={h}>{h}</option>))}
                    </select>
                    <span>시</span>
                    <select className="h-9 rounded-md border border-slate-200 bg-white px-2 focus:outline-none focus:ring-2 focus:ring-indigo-500" value={deliveryMinute} onChange={(e) => setDeliveryMinute(e.target.value)}>
                      {['00', '10', '20', '30', '40', '50'].map(m => (<option key={m} value={m}>{m}</option>))}
                    </select>
                    <span>분</span>
                  </div>
                )}

                <div className="flex items-center gap-2 before:content-['/'] before:text-slate-300 before:mx-1">
                  <span className="font-bold text-slate-700">행사시간</span>
                  <Input value={eventTime} onChange={(e) => setEventTime(e.target.value)} placeholder="예: 오후 2시 예식" className="w-48 bg-white h-9" />
                </div>
              </div>
            </section>

            {/* 3. 메시지 관리 영역 */}
            <section className="border-b border-slate-100 pb-8">
              <h3 className="text-lg font-bold mb-5 flex items-center gap-2">
                <span className="w-1.5 h-4 bg-slate-900 rounded-full inline-block"></span>
                메시지 (리본/카드)
              </h3>

              <div className="bg-slate-50 border border-slate-200 p-6 rounded-2xl shadow-sm">
                <div className="flex items-center gap-6 mb-6">
                  <label className="flex items-center gap-2 cursor-pointer font-medium text-slate-700">
                    <input type="radio" name="msgType" value="none" checked={messageType === "none"} onChange={() => setMessageType("none")} className="w-4 h-4 text-emerald-600 focus:ring-emerald-500" />
                    없음
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer font-medium text-slate-700">
                    <input type="radio" name="msgType" value="ribbon" checked={messageType === "ribbon"} onChange={() => setMessageType("ribbon")} className="w-4 h-4 text-emerald-600 focus:ring-emerald-500" />
                    리본
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer font-medium text-slate-700">
                    <input type="radio" name="msgType" value="card" checked={messageType === "card"} onChange={() => setMessageType("card")} className="w-4 h-4 text-emerald-600 focus:ring-emerald-500" />
                    카드
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer font-medium text-slate-700">
                    <input type="radio" name="msgType" value="ribbon_card" checked={messageType === "ribbon_card"} onChange={() => setMessageType("ribbon_card")} className="w-4 h-4 text-emerald-600 focus:ring-emerald-500" />
                    리본+카드
                  </label>
                </div>

                {["ribbon", "ribbon_card"].includes(messageType) && (
                  <div className="border-2 border-emerald-400 bg-white p-5 rounded-xl mb-4 relative">
                    <div className="absolute -top-3 left-4 bg-white px-2 text-sm font-bold text-emerald-600">리본 정보 입력</div>

                    <div className="flex flex-col gap-3 mb-6 mt-2">
                      <div className="flex items-center gap-3">
                        <span className="text-indigo-600 font-bold w-16 text-center">경조사어</span>
                        <Button type="button" size="sm" onClick={() => setIsPhraseModalOpen(true)} className="bg-indigo-600 hover:bg-indigo-700">
                          리본문구관리(경조사어)
                        </Button>
                        <Input value={ribbonPhrase} onChange={(e) => setRibbonPhrase(e.target.value)} placeholder="직접 입력하거나 목록에서 선택하세요" className="flex-1" />
                      </div>

                      <div className="flex flex-wrap gap-2 pl-[90px]">
                        {QUICK_PHRASES.map((phrase, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => setRibbonPhrase(phrase)}
                            className="border border-slate-200 bg-slate-50 text-slate-600 px-3 py-1.5 text-xs rounded-md hover:bg-emerald-50 hover:border-emerald-200 hover:text-emerald-700 transition-colors"
                          >
                            {phrase}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="h-px bg-slate-100 w-full mb-6"></div>

                    <div className="flex items-center gap-3">
                      <span className="text-indigo-600 font-bold w-16 text-center">보내는분</span>
                      <Button type="button" size="sm" onClick={() => setIsSenderModalOpen(true)} className="bg-indigo-600 hover:bg-indigo-700">
                        리본문구관리(명의)
                      </Button>
                      <Input value={ribbonSender} onChange={(e) => setRibbonSender(e.target.value)} placeholder="직접 입력하거나 목록에서 선택하세요" className="flex-1" />
                    </div>
                  </div>
                )}

                {["card", "ribbon_card"].includes(messageType) && (
                  <div className="border-2 border-emerald-400 bg-white p-5 rounded-xl relative">
                    <div className="absolute -top-3 left-4 bg-white px-2 text-sm font-bold text-emerald-600">카드 내용 입력</div>
                    <textarea
                      value={cardContent}
                      onChange={(e) => setCardContent(e.target.value)}
                      placeholder="카드내용을 입력 해 주세요"
                      className="w-full mt-2 border border-slate-200 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none min-h-[100px]"
                    />
                  </div>
                )}
              </div>
            </section>

            {/* 4. 주문 상품 정보 */}
            <section className="border-b border-slate-100 pb-8">
              <div className="flex justify-between items-center mb-5">
                <h3 className="text-lg font-bold flex items-center gap-2">
                  <span className="w-1.5 h-4 bg-slate-900 rounded-full inline-block"></span>
                  주문 상품 정보
                </h3>
                <Button size="sm" onClick={() => setIsProductModalOpen(true)} className="gap-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl shadow-sm">
                  <Search size={16} /> 실제 상품 검색하여 추가
                </Button>
              </div>

              <div className="space-y-3">
                {orderItems.length === 0 ? (
                  <div className="text-center py-10 bg-slate-50 border border-dashed border-slate-200 rounded-2xl text-slate-500">
                    우측 상단의 버튼을 눌러 주문할 상품을 추가해주세요.
                  </div>
                ) : (
                  orderItems.map((item, idx) => (
                    <div key={idx} className="flex gap-3 items-center bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                      <div className="w-20">
                        <Label className="text-xs font-bold text-slate-500">상품ID</Label>
                        <Input value={item.productId} readOnly className="bg-slate-50 text-slate-500 cursor-not-allowed mt-1" />
                      </div>
                      <div className="flex-1">
                        <Label className="text-xs font-bold text-slate-500">주문 상품명 (스냅샷)</Label>
                        <Input value={item.productNameSnapshot} onChange={e => handleItemChange(idx, 'productNameSnapshot', e.target.value)} className="bg-white mt-1" />
                      </div>
                      <div className="w-32">
                        <Label className="text-xs font-bold text-slate-500">단가 (원)</Label>
                        <Input type="number" value={item.productPriceSnapshot} onChange={e => handleItemChange(idx, 'productPriceSnapshot', Number(e.target.value))} className="bg-white mt-1" />
                      </div>
                      <div className="w-24">
                        <Label className="text-xs font-bold text-slate-500">수량</Label>
                        <Input type="number" min="1" value={item.quantity} onChange={e => handleItemChange(idx, 'quantity', Number(e.target.value))} className="bg-white mt-1" />
                      </div>
                      <div className="w-32 text-right">
                        <Label className="text-xs font-bold text-slate-500 block mb-3">합계</Label>
                        <span className="font-bold text-slate-900">{(item.productPriceSnapshot * item.quantity).toLocaleString()}원</span>
                      </div>
                      <div className="w-10 text-right mt-6">
                        <Button variant="ghost" className="text-rose-500 hover:text-rose-700 hover:bg-rose-50 p-2" onClick={() => removeItem(idx)}>
                          <Trash2 size={18} />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </section>

            {/* 5. 결제 수단 선택 및 금액 */}
            <section className="flex flex-col md:flex-row gap-6">
              <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200 flex-1">
                <h3 className="text-lg font-bold mb-4 text-slate-900">결제 수단 선택</h3>
                <RadioGroup value={formData.paymentMethod} onValueChange={(v) => setFormData({...formData, paymentMethod: v})} className="flex flex-col gap-4">
                  <div className="flex items-center gap-3">
                    <RadioGroupItem value="card" id="card" />
                    <Label htmlFor="card" className="font-bold text-base cursor-pointer">신용카드 (PG 실결제 연동)</Label>
                  </div>
                  {/*<div className="flex items-center gap-3">*/}
                  {/*  <RadioGroupItem value="offline" id="offline" />*/}
                  {/*  <Label htmlFor="offline" className="font-bold text-base cursor-pointer">무통장 / 수기결제 (PG 생략)</Label>*/}
                  {/*</div>*/}
                </RadioGroup>
              </div>

              <div className="bg-slate-900 text-white p-6 rounded-2xl border border-slate-800 flex-1 flex flex-col justify-center">
                <div className="flex justify-between items-center text-slate-300 mb-2">
                  <span>총 상품금액</span><span>{productAmount.toLocaleString()}원</span>
                </div>
                <div className="flex justify-between items-center text-slate-300 mb-2">
                  <span>배송비</span>
                  <Input type="number" value={formData.shippingAmount} onChange={e => setFormData({...formData, shippingAmount: Number(e.target.value)})} className="w-24 h-8 bg-slate-800 border-slate-700 text-white text-right focus:border-slate-500" />
                </div>
                <div className="flex justify-between items-center text-slate-300 mb-4">
                  <span>할인금액</span>
                  <Input type="number" value={formData.discountAmount} onChange={e => setFormData({...formData, discountAmount: Number(e.target.value)})} className="w-24 h-8 bg-slate-800 border-slate-700 text-white text-right focus:border-slate-500" />
                </div>
                <div className="flex justify-between items-center border-t border-slate-700 pt-4">
                  <span className="font-bold text-lg">최종 결제 금액</span>
                  <span className="text-3xl font-extrabold text-indigo-400">{totalAmount.toLocaleString()}원</span>
                </div>
              </div>
            </section>

            {/* 6. 액션 버튼 */}
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
              <Button variant="outline" className="h-14 px-8 rounded-xl bg-white" onClick={() => navigate(-1)}>취소</Button>
              <Button
                className="h-14 px-10 rounded-xl bg-indigo-600 text-lg shadow-md hover:bg-indigo-700 text-white"
                onClick={handlePayment}
                disabled={submitting}
              >
                {submitting ? "처리 중..." : "결제하기"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ============================================================== */}
      {/* 팝업 모달 영역 복구 (경조사어, 명의, 상품검색 전체 클래스 포함)       */}
      {/* ============================================================== */}

      {/* 모달 1: 경조사어 문구 관리 팝업 */}
      {isPhraseModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[80vh] animate-in fade-in zoom-in-95 duration-200">
            <div className="bg-emerald-600 p-4 flex justify-between items-center text-white">
              <h2 className="font-bold text-lg">리본문구(경조사어) 관리</h2>
              <button onClick={() => setIsPhraseModalOpen(false)} className="hover:text-emerald-200"><X size={20} /></button>
            </div>
            <div className="p-4 bg-slate-50 border-b flex gap-2">
              <Input placeholder="문구 검색..." className="bg-white" />
              <Button className="bg-slate-800 text-white">검색</Button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              <table className="w-full text-sm text-left">
                <thead className="bg-slate-100 text-slate-600 font-bold border-b">
                <tr>
                  <th className="p-3">분류</th>
                  <th className="p-3">내용</th>
                  <th className="p-3 text-center w-24">선택</th>
                </tr>
                </thead>
                <tbody>
                {QUICK_PHRASES.slice(0, 8).map((phrase, idx) => (
                  <tr key={idx} className="border-b hover:bg-slate-50">
                    <td className="p-3 text-emerald-600 font-medium">자주쓰는 경조사어</td>
                    <td className="p-3 text-slate-800">{phrase}</td>
                    <td className="p-3 text-center">
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-emerald-500 text-emerald-600 hover:bg-emerald-50"
                        onClick={() => {
                          setRibbonPhrase(phrase);
                          setIsPhraseModalOpen(false);
                        }}
                      >
                        선택
                      </Button>
                    </td>
                  </tr>
                ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* 모달 2: 보내는분(명의) 관리 팝업 */}
      {isSenderModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[80vh] animate-in fade-in zoom-in-95 duration-200">
            <div className="bg-emerald-600 p-4 flex justify-between items-center text-white">
              <h2 className="font-bold text-lg">리본 보내는분(명의) 관리</h2>
              <button onClick={() => setIsSenderModalOpen(false)} className="hover:text-emerald-200"><X size={20} /></button>
            </div>
            <div className="p-4 bg-slate-50 border-b flex gap-2">
              <Input placeholder="추가할 명의를 입력 해 주세요" className="bg-white" />
              <Button className="bg-slate-800 text-white">추가</Button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              <ul className="space-y-2">
                {["녹십자의료재단 소장 임용규", "도원초등학교 제 25회 동창회 일동", "송면중 9회 동창회", "이로운꽃집", "(주)서룡상사 대표이사 서용문"].map((sender, idx) => (
                  <li key={idx} className="flex items-center justify-between p-3 border rounded-lg hover:border-emerald-500 hover:bg-emerald-50 transition-colors">
                    <span className="font-medium text-slate-700">{sender}</span>
                    <Button
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => {
                        setRibbonSender(sender);
                        setIsSenderModalOpen(false);
                      }}
                    >
                      선택
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* 모달 3: 상품 검색 모달 */}
      {isProductModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
          <div className="w-full max-w-2xl rounded-[28px] bg-white shadow-2xl flex flex-col max-h-[85vh] overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between p-6 border-b border-slate-100 bg-slate-50/50">
              <h2 className="text-xl font-bold text-slate-900">상품 검색 및 추가</h2>
              <button onClick={() => setIsProductModalOpen(false)} className="text-slate-400 hover:text-slate-700 bg-white rounded-full p-1 border border-slate-200 hover:bg-slate-100 transition-colors">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 border-b border-slate-100 bg-white">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <Input
                    value={searchKeyword}
                    onChange={e => setSearchKeyword(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSearchProducts()}
                    placeholder="상품명 또는 상품 코드를 입력하세요"
                    className="pl-10 h-12 bg-slate-50 border-slate-200 focus:bg-white text-base rounded-xl"
                  />
                </div>
                <Button onClick={handleSearchProducts} disabled={isSearching} className="h-12 px-6 rounded-xl bg-slate-900 text-white hover:bg-slate-800">
                  {isSearching ? "검색중..." : "검색"}
                </Button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto bg-slate-50/50 flex-1">
              {searchResults.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  {searchKeyword ? "검색 결과가 없습니다." : "검색어를 입력하고 엔터를 쳐주세요."}
                </div>
              ) : (
                <div className="space-y-3">
                  {searchResults.map((product) => (
                    <div key={product.productId} className="flex justify-between items-center bg-white p-4 rounded-2xl border border-slate-200 hover:border-indigo-300 hover:shadow-md transition-all">
                      <div className="flex items-center gap-4">
                        {product.thumbnailUrl ? (
                          <img src={product.thumbnailUrl} alt={product.productName} className="w-16 h-16 rounded-xl object-cover border border-slate-100" />
                        ) : (
                          <div className="w-16 h-16 rounded-xl bg-slate-100 flex items-center justify-center text-xs text-slate-400 border border-slate-200">No Img</div>
                        )}
                        <div>
                          <p className="text-xs font-bold text-slate-400 mb-1">
                            [ID: {product.productId}] {product.productCode ? `· 코드: ${product.productCode}` : ''}
                          </p>
                          <p className="font-bold text-slate-900 text-base">{product.productName}</p>
                          <div className="flex items-center gap-3 mt-1">
                            <span className="text-sm font-bold text-indigo-600">{product.price?.toLocaleString()}원</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${product.stockQuantity > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                              재고: {product.stockQuantity}개
                            </span>
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        onClick={() => handleSelectProduct(product)}
                        disabled={product.stockQuantity <= 0}
                        className={`rounded-xl px-6 ${product.stockQuantity > 0 ? 'hover:bg-indigo-50 border-indigo-200 text-indigo-700' : 'opacity-50'}`}
                      >
                        {product.stockQuantity > 0 ? '선택' : '품절'}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  )
}